import {
  faUser,
  faCheck,
  faFilter,
  faInfoCircle,
  faPen,
  faPlus,
  faSearch,
  faTimes,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faUsers,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAuthenticatedAPI } from "../hooks/useAuthenticatedAPI";
import ProcessLoading from "../components/processLoading";
import Select from "react-select";
import "../styles/gestionArticle.css";

const AdminUser = () => {
  const { client, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { apiGet, apiPost, apiPut, apiDelete, API_BASE_URL } =
    useAuthenticatedAPI();

  // États principaux
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  // États pour les filtres et recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [sexeFilter, setSexeFilter] = useState("Tous");
  const [roleFilter, setRoleFilter] = useState("Tous");
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterResults, setFilterResults] = useState([]);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // États pour le modal
  const [showModal, setShowModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // États de chargement et erreurs
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Options pour les sélecteurs
  //const sexeOptions = ["masculin", "feminin"];
  //const roleOptions = ["client", "admin", "super admin"];

  const sexeOptions = [
    { value: "masculin", label: "Masculin" },
    { value: "feminin", label: "Féminin" },
  ];

  const roleOptions = [
    { value: "client", label: "Client" },
    { value: "admin", label: "Admin" },
    { value: "super admin", label: "Super Admin" },
  ];
  // Options pour les filtres
  const sexeFilterOptions = [
    { value: "Tous", label: "Tous les sexes" },
    { value: "masculin", label: "Masculin" },
    { value: "feminin", label: "Féminin" },
  ];

  const roleFilterOptions = [
    { value: "Tous", label: "Tous les rôles" },
    { value: "client", label: "Client" },
    { value: "admin", label: "Admin" },
    { value: "super admin", label: "Super Admin" },
  ];

  // Vérification des autorisations au chargement
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/");
      return;
    }

    if (client?.role === "client") {
      setError(
        "Vous n'avez pas les autorisations nécessaires pour accéder à cette page"
      );
      setTimeout(() => navigate("/dashboard"), 2000);
      return;
    }

    loadInitialData();
  }, [client, isAuthenticated, navigate]);

  // Chargement initial des données
  const loadInitialData = async () => {
    setPageLoading(true);
    try {
      await fetchClients();
    } catch (err) {
      setError("Erreur lors du chargement des données");
      console.error("Erreur:", err);
    } finally {
      setPageLoading(false);
    }
  };

  // Récupérer tous les clients
  const fetchClients = async () => {
    try {
      const data = await apiGet("/client/get");
      const clientsArray = Array.isArray(data.clients)
        ? data.clients
        : Array.isArray(data)
        ? data
        : [];
      setClients(clientsArray);
      if (clientsArray.length > 0 && !selectedClient) {
        setSelectedClient(clientsArray[0]);
      }
    } catch (err) {
      console.error("Erreur fetch clients:", err);
      setError(err.message || "Erreur lors de la récupération des clients");
    }
  };

  // Recherche frontend de clients
  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setIsSearching(false);
      setIsFiltering(false);
      setFilterResults([]);
      return;
    }

    // Recherche frontend dans tous les clients
    const searchResults = clients.filter(
      (client) =>
        client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.adresse.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.tel.toString().includes(searchTerm)
    );

    setFilterResults(searchResults);
    setIsSearching(true);
    setIsFiltering(false);
    setCurrentPage(1);
  };

  // Filtrage par sexe - MODIFIÉ POUR REACT-SELECT
  const handleSexeFilter = (selected) => {
    const sexe = selected?.value || "Tous";
    setSexeFilter(sexe);

    if (sexe === "Tous") {
      setIsFiltering(false);
      setFilterResults([]);
      setIsSearching(false);
      return;
    }

    const filteredResults = clients.filter((client) => client.sexe === sexe);
    setFilterResults(filteredResults);
    setIsFiltering(true);
    setIsSearching(false);
    setCurrentPage(1);
  };

  // Filtrage par rôle - MODIFIÉ POUR REACT-SELECT
  const handleRoleFilter = (selected) => {
    const role = selected?.value || "Tous";
    setRoleFilter(role);

    if (role === "Tous") {
      setIsFiltering(false);
      setFilterResults([]);
      setIsSearching(false);
      return;
    }

    const filteredResults = clients.filter((client) => client.role === role);
    setFilterResults(filteredResults);
    setIsFiltering(true);
    setIsSearching(false);
    setCurrentPage(1);
  };

  // Nouvelle fonction pour gérer les selects react-select
  const handleSelectChange = (selected, actionMeta) => {
    const { name } = actionMeta;
    setCurrentClient((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));
  };

  // Réinitialiser la recherche et les filtres
  const clearSearch = () => {
    setSearchTerm("");
    setIsSearching(false);
    setIsFiltering(false);
    setFilterResults([]);
    setSexeFilter("Tous");
    setRoleFilter("Tous");
    setCurrentPage(1);
  };

  // Obtenir les clients à afficher (recherche, filtre ou tous)
  const getDisplayedClients = () => {
    if (isSearching || isFiltering) return filterResults;
    return clients;
  };

  // Pagination
  const displayedClients = getDisplayedClients();
  const totalPages = Math.ceil(displayedClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = displayedClients.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Fonctions du modal
  const openCreateModal = () => {
    setIsEditing(false);
    setCurrentClient({
      nom: "",
      prenom: "",
      email: "",
      password: "",
      confirmPassword: "",
      tel: "",
      adresse: "",
      sexe: "",
      role: "client",
      image: null,
    });
    setPreviewImage(null);
    setShowPassword(false); // ✅ RÉINITIALISER
    setShowConfirmPassword(false); // ✅ RÉINITIALISER
    setShowModal(true);
  };

  const openEditModal = (clientData) => {
    setIsEditing(true);
    setCurrentClient({
      ...clientData,
      password: "", // On ne prérempli pas le mot de passe pour la sécurité
      confirmPassword: "",
    });
    setPreviewImage(
      clientData.image
        ? `https://boutiqu.transfoafricainc.com/${clientData.image}`
        : null
    );
    setShowPassword(false); // ✅ RÉINITIALISER
    setShowConfirmPassword(false); // ✅ RÉINITIALISER
    setShowModal(true);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentClient((prev) => ({
        ...prev,
        image: file,
      }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // Validation email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation mot de passe
  const isValidPassword = (password) => {
    // Au moins 8 caractères, au moins une majuscule, au moins un chiffre, aucun caractère spécial
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/;
    return passwordRegex.test(password);
  };

  // Sauvegarde
  const handleSave = async () => {
    setLoading(true);
    try {
      // Validation
      if (
        !currentClient.nom ||
        !currentClient.prenom ||
        !currentClient.email ||
        !currentClient.tel ||
        !currentClient.adresse ||
        !currentClient.sexe
      ) {
        setError("Veuillez remplir tous les champs obligatoires");
        setLoading(false);
        return;
      }

      if (!isValidEmail(currentClient.email)) {
        setError("Veuillez saisir un email valide");
        setLoading(false);
        return;
      }

      if (!isEditing && !currentClient.password) {
        setError(
          "Le mot de passe est obligatoire pour créer un nouveau client"
        );
        setLoading(false);
        return;
      }

      if (currentClient.password && !isValidPassword(currentClient.password)) {
        setError(
          "Le mot de passe doit avoir au moins 8 caractères, au moins une lettre majuscule, au moins un chiffre et aucun caractère spécial"
        );
        setLoading(false);
        return;
      }

      if (
        currentClient.password &&
        currentClient.password !== currentClient.confirmPassword
      ) {
        setError("Les mots de passe ne correspondent pas");
        setLoading(false);
        return;
      }

      const formData = new FormData();

      formData.append("nom", currentClient.nom.trim());
      formData.append("prenom", currentClient.prenom.trim());
      formData.append("email", currentClient.email.trim());
      formData.append("tel", parseInt(currentClient.tel));
      formData.append("adresse", currentClient.adresse.trim());
      formData.append("sexe", currentClient.sexe);
      formData.append("role", currentClient.role);

      // Ajouter le mot de passe ET confirmPassword seulement s'ils sont fournis
      if (currentClient.password) {
        formData.append("password", currentClient.password);
        formData.append("confirmPassword", currentClient.confirmPassword); // ✅ AJOUTÉ
      }

      if (currentClient.image instanceof File) {
        formData.append("image", currentClient.image);
      }

      let response;
      if (isEditing) {
        const clientId = currentClient.id_client;
        if (!clientId) {
          throw new Error("ID du client non trouvé pour la modification");
        }
        response = await apiPut(`/client/put/${clientId}`, formData);
      } else {
        response = await apiPost("/client/post", formData);
      }

      setSuccess(
        isEditing ? "Client modifié avec succès" : "Client créé avec succès"
      );
      setShowModal(false);

      // Réinitialiser les états des mots de passe
      setShowPassword(false);
      setShowConfirmPassword(false);

      await fetchClients();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
      if (err.message && err.message.includes("Session expirée")) {
        setError("Votre session a expiré. Veuillez vous reconnecter.");
        setTimeout(() => {
          localStorage.removeItem("token");
          navigate("/");
        }, 2000);
      } else {
        setError(err.message || "Erreur lors de la sauvegarde");
      }
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Suppression
  const handleDelete = async (id_client) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce client ?")) return;

    setLoading(true);
    try {
      await apiDelete(`/client/del/${id_client}`);
      setSuccess("Client supprimé avec succès");

      if (selectedClient?.id_client === id_client) {
        setSelectedClient(
          clients.length > 1
            ? clients.find((c) => c.id_client !== id_client)
            : null
        );
      }

      await fetchClients();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      if (err.message && err.message.includes("Session expirée")) {
        setError("Votre session a expiré. Veuillez vous reconnecter.");
        setTimeout(() => {
          localStorage.removeItem("token");
          navigate("/");
        }, 2000);
      } else {
        setError(err.message || "Erreur lors de la suppression");
      }
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Obtenir le badge de rôle avec couleur
  const getRoleBadge = (role) => {
    const badges = {
      client: "bg-primary",
      admin: "bg-warning text-dark",
      "super admin": "bg-danger",
    };
    return badges[role] || "bg-secondary";
  };

  // Formatage de la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  if (pageLoading) {
    return <ProcessLoading message="Chargement des clients..." />;
  }

  return (
    <div className="container-fluid p-4 bg-light">
      {/* Messages d'erreur et succès */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      {success && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          ></button>
        </div>
      )}

      {/* En-tête */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
            Gestion des Clients (Administration)
          </h4>
          <small className="text-muted">
            {isSearching
              ? `${displayedClients.length} résultat(s) de recherche`
              : isFiltering
              ? `${displayedClients.length} client(s) filtré(s)`
              : `${displayedClients.length} client(s)`}{" "}
            • Page {currentPage}/{totalPages}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nouveau Client
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Rechercher & Filtrer
          </h5>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
                {(isSearching || isFiltering) && (
                  <button className="btn btn-secondary" onClick={clearSearch}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <Select
                options={sexeFilterOptions}
                value={
                  sexeFilterOptions.find(
                    (option) => option.value === sexeFilter
                  ) || sexeFilterOptions[0]
                }
                onChange={handleSexeFilter}
                placeholder="Filtrer par sexe"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                isSearchable={false}
              />
            </div>
            <div className="col-md-4">
              <Select
                options={roleFilterOptions}
                value={
                  roleFilterOptions.find(
                    (option) => option.value === roleFilter
                  ) || roleFilterOptions[0]
                }
                onChange={handleRoleFilter}
                placeholder="Filtrer par rôle"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Message si aucun résultat */}
      {(isSearching || isFiltering) && displayedClients.length === 0 && (
        <div className="alert alert-info text-center">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          {isSearching
            ? `Aucun client trouvé pour "${searchTerm}"`
            : `Aucun client trouvé pour le filtre sélectionné`}
        </div>
      )}

      {/* Contenu principal */}
      <div className="row">
        {/* Liste des clients */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Photo</th>
                      <th>Nom Complet</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                      <th>Sexe</th>
                      <th>Rôle</th>
                      <th>Inscription</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients.map((client) => (
                      <tr
                        key={client.id_client}
                        onClick={() => setSelectedClient(client)}
                        className={
                          selectedClient?.id_client === client.id_client
                            ? "table-primary"
                            : ""
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="fw-bold">{client.id_client}</td>
                        <td>
                          {client.image &&
                          client.image !==
                            "https://boutiqu.transfoafricainc.com/uploads/profil.png" ? (
                            <img
                              src={`https://boutiqu.transfoafricainc.com/${client.image}`}
                              alt={`${client.nom} ${client.prenom}`}
                              style={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                              className="rounded-circle border"
                            />
                          ) : (
                            <div
                              className="bg-light rounded-circle border d-flex align-items-center justify-content-center"
                              style={{ width: "50px", height: "50px" }}
                            >
                              <FontAwesomeIcon
                                icon={faUser}
                                className="text-muted"
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="fw-bold">
                            {client.nom} {client.prenom}
                          </div>
                          <small className="text-muted">
                            {client.adresse?.substring(0, 30)}...
                          </small>
                        </td>
                        <td>{client.email}</td>
                        <td>{client.tel}</td>
                        <td>
                          <span className="badge bg-info">
                            {client.sexe?.charAt(0).toUpperCase() +
                              client.sexe?.slice(1)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${getRoleBadge(client.role)}`}
                          >
                            {client.role?.charAt(0).toUpperCase() +
                              client.role?.slice(1)}
                          </span>
                        </td>
                        <td>{formatDate(client.createdAt)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(client.id_client);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(client);
                            }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Affichage de {startIndex + 1} à{" "}
                    {Math.min(
                      startIndex + itemsPerPage,
                      displayedClients.length
                    )}
                    sur {displayedClients.length} clients
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <li
                              key={page}
                              className={`page-item ${
                                currentPage === page ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        } else if (
                          page === currentPage - 3 ||
                          page === currentPage + 3
                        ) {
                          return (
                            <li key={page} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}

                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Détails du client */}
        <div className="col-lg-4">
          {selectedClient ? (
            <div className="card shadow-sm h-100">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Détails du client</h5>
              </div>
              <div className="card-body">
                <div className="text-center mb-3">
                  {selectedClient.image &&
                  selectedClient.image !==
                    "https://boutiqu.transfoafricainc.com/uploads/profil.png" ? (
                    <img
                      src={`https://boutiqu.transfoafricainc.com/${selectedClient.image}`}
                      alt={`${selectedClient.nom} ${selectedClient.prenom}`}
                      className="img-fluid rounded-circle border"
                      style={{
                        maxHeight: "150px",
                        width: "150px",
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                    />
                  ) : (
                    <div
                      className="bg-light rounded-circle border d-inline-flex align-items-center justify-content-center"
                      style={{ width: "150px", height: "150px" }}
                    >
                      <FontAwesomeIcon
                        icon={faUser}
                        size="3x"
                        className="text-secondary"
                      />
                    </div>
                  )}
                </div>

                <h5 className="text-primary text-center">
                  {selectedClient.nom} {selectedClient.prenom}
                </h5>
                <p className="text-muted text-center mb-3">
                  <span
                    className={`badge ${getRoleBadge(selectedClient.role)}`}
                  >
                    {selectedClient.role?.charAt(0).toUpperCase() +
                      selectedClient.role?.slice(1)}
                  </span>
                </p>

                <div className="mb-3">
                  <h6 className="text-muted">INFORMATIONS PERSONNELLES</h6>
                  <hr className="mt-1" />
                  <div className="row">
                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">ID Client</small>
                      <span>{selectedClient.id_client}</span>
                    </div>
                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">
                        <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                        Email
                      </small>
                      <span>{selectedClient.email}</span>
                    </div>
                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">
                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                        Téléphone
                      </small>
                      <span>{selectedClient.tel}</span>
                    </div>
                    <div className="col-12 mb-2">
                      <small className="text-muted d-block">
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="me-1"
                        />
                        Adresse
                      </small>
                      <span>{selectedClient.adresse}</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Sexe</small>
                      <span className="badge bg-info">
                        {selectedClient.sexe?.charAt(0).toUpperCase() +
                          selectedClient.sexe?.slice(1)}
                      </span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Inscription</small>
                      <span>{formatDate(selectedClient.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-2 mt-4">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => openEditModal(selectedClient)}
                  >
                    <FontAwesomeIcon icon={faPen} className="me-2" />
                    Modifier le client
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => handleDelete(selectedClient.id_client)}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Supprimer le client
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm h-100">
              <div className="card-body d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0">
                  Sélectionnez un client pour afficher les détails
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour ajouter/modifier */}
      {showModal && (
        <div className="modal-overlay" style={{ zIndex: 1050 }}>
          <div
            className="modal-content"
            style={{ maxWidth: "700px", zIndex: 1051 }}
          >
            <div className="modal-header">
              <h5>{isEditing ? "Modifier le client" : "Ajouter un client"}</h5>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Nom <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="nom"
                      value={currentClient.nom}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Prénom <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="prenom"
                      value={currentClient.prenom}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={currentClient.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Mot de passe{" "}
                      {!isEditing && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        name="password"
                        value={currentClient.password}
                        onChange={handleInputChange}
                        placeholder={
                          isEditing
                            ? "Laisser vide pour conserver l'ancien"
                            : ""
                        }
                        required={!isEditing}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                    <small className="form-text text-muted">
                      Au moins 8 caractères, au moins une lettre majuscule, au
                      moins un chiffre, aucun caractère spécial
                    </small>
                  </div>

                  {/* Champ confirmer mot de passe - AMÉLIORÉ */}
                  {(currentClient.password || !isEditing) && (
                    <div className="mb-3">
                      <label className="form-label">
                        Confirmer le mot de passe{" "}
                        {!isEditing && <span className="text-danger">*</span>}
                      </label>
                      <div className="input-group">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          name="confirmPassword"
                          value={currentClient.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirmer le mot de passe"
                          required={!isEditing || currentClient.password}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                          />
                        </button>
                      </div>
                      {/* Indicateur de correspondance des mots de passe */}
                      {currentClient.password &&
                        currentClient.confirmPassword && (
                          <small
                            className={`form-text ${
                              currentClient.password ===
                              currentClient.confirmPassword
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {currentClient.password ===
                            currentClient.confirmPassword
                              ? "✓ Les mots de passe correspondent"
                              : "✗ Les mots de passe ne correspondent pas"}
                          </small>
                        )}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">
                      Téléphone <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      name="tel"
                      value={currentClient.tel}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  {previewImage && (
                    <div className="mb-3 text-center">
                      <img
                        src={previewImage}
                        alt="Aperçu"
                        className="img-fluid rounded-circle border"
                        style={{
                          maxHeight: "150px",
                          width: "150px",
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Photo de profil</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Adresse <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      name="adresse"
                      rows="3"
                      value={currentClient.adresse}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Sexe <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={sexeOptions}
                        value={
                          currentClient.sexe
                            ? sexeOptions.find(
                                (option) => option.value === currentClient.sexe
                              )
                            : null
                        }
                        onChange={(selected) =>
                          handleSelectChange(selected, { name: "sexe" })
                        }
                        placeholder="Selectionner"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base, state) => ({
                            ...base,
                            borderColor: state.isFocused
                              ? "#0d6efd"
                              : "#dee2e6",
                            boxShadow: state.isFocused
                              ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                              : "none",
                            "&:hover": {
                              borderColor: "#0d6efd",
                            },
                          }),
                        }}
                        name="sexe"
                        isSearchable={false}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Rôle <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={roleOptions}
                        value={
                          currentClient.role
                            ? roleOptions.find(
                                (option) => option.value === currentClient.role
                              )
                            : null
                        }
                        onChange={(selected) =>
                          handleSelectChange(selected, { name: "role" })
                        }
                        placeholder="Selectionner"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base, state) => ({
                            ...base,
                            borderColor: state.isFocused
                              ? "#0d6efd"
                              : "#dee2e6",
                            boxShadow: state.isFocused
                              ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
                              : "none",
                            "&:hover": {
                              borderColor: "#0d6efd",
                            },
                          }),
                        }}
                        name="role"
                        isSearchable={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={
                  loading ||
                  !currentClient.nom ||
                  !currentClient.prenom ||
                  !currentClient.email ||
                  !currentClient.tel ||
                  !currentClient.adresse ||
                  !currentClient.sexe ||
                  !currentClient.role ||
                  // ✅ VALIDATION SUPPLÉMENTAIRE pour les mots de passe
                  (!isEditing && !currentClient.password) ||
                  (!isEditing && !currentClient.confirmPassword) ||
                  (currentClient.password &&
                    currentClient.password !== currentClient.confirmPassword)
                }
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {isEditing ? "Modification..." : "Création..."}
                  </>
                ) : (
                  <>{isEditing ? "Enregistrer" : "Ajouter"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <ProcessLoading message="Traitement en cours..." />}
    </div>
  );
};

export default AdminUser;
